/**
 * SeatPeek — a court EXHIBIT of a seat's real surface (K3 direction 2026-10-05).
 * Square, ruled, flat, tabular — Bloomberg terminal / court exhibit, NOT a fake
 * app window. No chrome dots, no tabs, no pills. The ONLY orange atom here is the
 * decisive figure (the total / accent cell). Data + grounding: seatPeeks.ts.
 *
 * Token-only (design law): colors via semantic tokens / CSS vars, so light and
 * dark are automatic. The accounting double rule under a total is a forensic
 * convention — rendered with --color-border-strong (its documented purpose).
 */

import type { SeatPeek as SeatPeekData, LedgerRow, RegisterRow, StatCell, PeekTotal } from './seatPeeks';

const HAIR = { borderColor: 'var(--color-hairline)' } as const;
// Accounting double rule above a total — the ledger tell. K3: must read clearly,
// so a lighter grey token than the subtle border-strong, kept greyscale (no hue).
const DOUBLE_RULE = { borderTop: '4px double var(--color-text-dim)' } as const;

const REGISTER_STYLE: Record<RegisterRow['state'], { glyph: string; cls: string }> = {
  approved: { glyph: '✓', cls: 'text-text-muted' },
  review: { glyph: '●', cls: 'text-text-primary' },
  todo: { glyph: '○', cls: 'text-text-dim' },
};

function TotalRow({ total }: { total: PeekTotal }) {
  return (
    <div className="mt-2 flex items-baseline justify-between gap-4 py-4" style={DOUBLE_RULE}>
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-text-muted">{total.label}</span>
      <span className="shrink-0 font-mono text-xl tabular-nums text-brand-text">{total.value}</span>
    </div>
  );
}

function Ledger({ rows, total }: { rows: LedgerRow[]; total?: PeekTotal }) {
  return (
    <div className="py-1">
      {rows.map((r) => (
        <div key={r.label} className="flex items-baseline justify-between gap-4 border-b py-3" style={HAIR}>
          <span className="min-w-0 font-body text-[13px] leading-snug text-text-secondary">{r.label}</span>
          <span
            className={`shrink-0 font-mono text-sm tabular-nums ${r.muted ? 'text-text-dim' : 'text-text-primary'}`}
          >
            {r.value}
          </span>
        </div>
      ))}
      {total && <TotalRow total={total} />}
    </div>
  );
}

function Register({
  rows,
  attested,
  total,
}: {
  rows: RegisterRow[];
  attested?: LedgerRow[];
  total?: PeekTotal;
}) {
  return (
    <div className="py-1">
      {rows.map((r) => {
        const s = REGISTER_STYLE[r.state];
        return (
          <div key={r.label} className="flex items-center justify-between gap-4 border-b py-2.5" style={HAIR}>
            <span className="flex min-w-0 items-center gap-2.5">
              <span className={`font-mono text-xs ${s.cls}`} aria-hidden="true">
                {s.glyph}
              </span>
              <span className="truncate font-body text-[13px] text-text-secondary">{r.label}</span>
            </span>
            <span className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.12em] ${s.cls}`}>{r.status}</span>
          </div>
        );
      })}

      {attested && attested.length > 0 && (
        <div className="mt-2 border-t pt-3" style={HAIR}>
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-text-muted">
            Attested <span style={{ color: 'var(--color-verified)' }}>✓</span>
          </p>
          <div className="mt-2 space-y-2">
            {attested.map((a) => (
              <div key={a.label} className="flex items-baseline justify-between gap-4">
                <span className="min-w-0 font-body text-[13px] text-text-secondary">{a.label}</span>
                <span
                  className={`shrink-0 font-mono tabular-nums ${a.accent ? 'text-base' : 'text-sm'}`}
                  style={{ color: a.accent ? 'var(--color-needs-human-text)' : 'var(--color-text-primary)' }}
                >
                  {a.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {total && <TotalRow total={total} />}
    </div>
  );
}

function StatStrip({ cells }: { cells: StatCell[] }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-5 py-5 sm:grid-cols-4 sm:gap-0">
      {cells.map((c, i) => (
        <div key={c.label} className={i > 0 ? 'sm:border-l sm:pl-4' : 'sm:pr-4'} style={HAIR}>
          <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-text-dim">{c.label}</div>
          <div
            className={`mt-1.5 text-right font-mono text-[22px] tabular-nums ${c.accent ? 'text-brand-text' : 'text-text-primary'}`}
          >
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SeatPeek({ peek, seatLabel }: { peek: SeatPeekData; seatLabel: string }) {
  return (
    <figure className="border bg-surface" style={HAIR} aria-label={`${seatLabel} — illustrative sample exhibit`}>
      {/* Exhibit header */}
      <div
        className="flex items-center justify-between gap-3 border-b px-5 py-3 font-mono text-[10px] uppercase tracking-[0.16em]"
        style={HAIR}
      >
        <span className="truncate text-text-muted">
          Exhibit {peek.exhibit} · {peek.source}
        </span>
        <span className="shrink-0 text-text-dim">Illustrative sample</span>
      </div>

      {/* Matter block */}
      <div className="flex items-start justify-between gap-4 border-b px-5 py-5" style={HAIR}>
        <div className="min-w-0">
          <h3 className="font-heading text-[22px] leading-tight text-text-primary">{peek.matterTitle}</h3>
          <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.08em] text-text-muted">{peek.matterMeta}</p>
        </div>
        {peek.status ? (
          <p className="shrink-0 text-right font-mono text-[10px] leading-relaxed">
            <span className="block uppercase tracking-[0.12em] text-text-dim">{peek.status.label}</span>
            <span className="text-text-secondary">{peek.status.value}</span>
          </p>
        ) : peek.stage ? (
          <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.12em] text-text-dim">{peek.stage}</span>
        ) : null}
      </div>

      {/* Decision zone */}
      <div className="px-5">
        {peek.pattern === 'ledger' && peek.ledgerRows && <Ledger rows={peek.ledgerRows} total={peek.total} />}
        {peek.pattern === 'register' && peek.registerRows && (
          <Register rows={peek.registerRows} attested={peek.attestedLines} total={peek.total} />
        )}
        {peek.pattern === 'stat' && peek.statCells && <StatStrip cells={peek.statCells} />}
      </div>

      {/* Provenance footer */}
      <div className="border-t px-5 py-3" style={HAIR}>
        <p className="font-mono text-[10px] uppercase tracking-[0.08em] leading-relaxed text-text-dim">
          {peek.provenance}
        </p>
      </div>
    </figure>
  );
}
